import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PasswordImg from '../../assets/icons/lock.svg';
import UserNameImg from '../../assets/icons/silhouette.svg';
import userImg from '../../assets/images/person-with-login-screen.svg';
import Loader from '../../components/Loader/Loader';
import { isTestApiUrlApplied, removeTestApiUrl, setTestApiUrl } from '../../utils/testApi';
import { isDevRelease } from '../../config/app';
import useForm from '../../utils/customHooks/useForm';
import { translate } from '../../utils/translate';
import { emailSchema } from '../../utils/validate';
import { setEmail, setPassword } from '../Login2fa/Login2faSlice';
import {
  SButton,
  SErrorText,
  SForgotPassLink,
  SForm,
  SPage,
  STextContainer,
  STextField,
  STitle,
  SUserImg,
} from './LegacyLogin.styles';
import {
  selectLoginError,
  selectLoginSliceIsLoading,
  setAlertMessage,
  setIsLoginError,
} from './LoginSlice';
import { TextFieldType } from '../../components/TextField/TextField';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { type RegionEnum } from '../../config/region';
import { RegionDropdown } from '../../components/RegionDropdown/RegionDropdown';
import { regionErrorTx, useLoginFlow, LoginStepEnum } from '../../utils/customHooks/useLoginFlow';

type LoginProps = {
  mode?: 'login' | 'sso';
};

export type LoginForm = {
  code?: string;
  email: string;
  password: string;
};

export type STextFieldParams = {
  error: boolean;
  disabled?: boolean;
};

export type SButtonParams = {
  $error?: boolean;
  $textOnly?: boolean;
  $cosafeLogin?: boolean;
  $ssoLogin?: boolean;
};

export const LegacyLogin = (props: LoginProps) => {
  const { mode = 'login' } = props;

  const isSsoLogin = mode === 'sso';
  const { inputs, handleChange, handleBlur, touched, isValid } = useForm<LoginForm>({
    initial: {
      email: '',
      password: '',
    },
    validateSchema: {
      email: emailSchema,
    },
  });

  const dispatch = useAppDispatch();
  const loginError = useAppSelector(selectLoginError);
  const isLoading = useAppSelector(selectLoginSliceIsLoading);
  const location = useLocation();

  useEffect(() => {
    dispatch(setAlertMessage(true));
    dispatch(setEmail(''));
    dispatch(setPassword(''));
    dispatch(setIsLoginError(null));
  }, [location, dispatch]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    handleChange(e as React.ChangeEvent<HTMLInputElement>);

  const [url, setUrl] = useState<string>();
  const handleApplyTestUrl = async () => {
    if (url) {
      setTestApiUrl(url);
      setUrl('');
      alert('done');
    }
  };

  const handleRemoveTestUrl = async () => {
    await removeTestApiUrl();
    // to force rerender component
    setUrl(prev => (prev === '' ? undefined : ''));
  };

  const onUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(e.target.value);
  };

  const { loginStepState, setSelectedRegion, handleSubmit } = useLoginFlow();

  const handleChangeRegion = (region: RegionEnum) => {
    dispatch(setIsLoginError(null));
    setSelectedRegion(region);
  };
  const showRegionDropdown =
    loginStepState.step === LoginStepEnum.Credentials && Array.isArray(loginStepState.regions);
  const showPasswordFields = loginStepState.step === LoginStepEnum.Credentials && !isSsoLogin;
  const isRegionError = loginError === regionErrorTx;
  const regionNotSelected =
    loginStepState.step === LoginStepEnum.Credentials &&
    !!loginStepState.regions?.length &&
    !loginStepState.selectedRegion;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = inputs.email.trim();

    handleSubmit(trimmedEmail, inputs.password);
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    inputs.email = inputs.email.trim();
    handleBlur(e as React.FocusEvent<HTMLInputElement>);
    dispatch(setIsLoginError(null));
  };

  if (isLoading) return <Loader />;
  return (
    <SPage>
      <SUserImg src={userImg} alt="" />
      <STitle>{translate('login_title')}</STitle>
      <SForm onSubmit={onSubmit}>
        <STextField
          isShadow
          error={!!loginError}
          StartAdornment={<img src={UserNameImg} alt="username icon" />}
          name="email"
          aria-label="email"
          className="email"
          placeHolderTx="login_email"
          value={inputs.email}
          onChange={onChange}
          onBlur={onBlur}
          touched={touched.email ?? false}
          disabled={showRegionDropdown}
          autoComplete="off"
        />
        {showRegionDropdown && (
          <RegionDropdown
            regions={Array.isArray(loginStepState.regions) ? loginStepState.regions : []}
            currentRegion={loginStepState.selectedRegion}
            onChangeRegion={handleChangeRegion}
            isError={isRegionError}
          />
        )}
        {showPasswordFields && (
          <STextField
            isShadow
            error={!!loginError}
            StartAdornment={<img src={PasswordImg} alt="password icon" />}
            type={TextFieldType.PASSWORD}
            aria-label="password"
            name="password"
            placeHolderTx="login_password"
            value={inputs.password}
            onChange={onChange}
            onBlur={onBlur}
            touched={touched.password ?? false}
            disabled={showRegionDropdown}
          />
        )}

        <STextContainer>
          {!!loginError && <SErrorText>{translate(loginError)}</SErrorText>}
          {showPasswordFields && (
            <SForgotPassLink to="../forgetPassword">
              {translate('login_forgot_password')}
            </SForgotPassLink>
          )}
        </STextContainer>

        <SButton
          aria-label="login"
          disabled={!isValid || regionNotSelected}
          type="submit"
          tx={'login_login'}
        />
      </SForm>

      {isDevRelease() && (
        <>
          <SButton
            onClick={handleRemoveTestUrl}
            disabled={!isTestApiUrlApplied()}
            text="Reset API_URL"
            $textOnly
          ></SButton>
          <STextField
            placeholder="Enter API_URL for testing"
            onChange={onUrlChange}
            value={url}
            autoCapitalize="none"
            error={false}
          />
          <SButton
            onClick={handleApplyTestUrl}
            disabled={!url}
            text="Apply API_URL"
            $textOnly
          ></SButton>
        </>
      )}
    </SPage>
  );
};
